import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import { Helmet } from '@xing-com/crate-helmet';
import {
  useBasePath,
  useIsCoverLetterEditor,
  useIsCoverLetterRoot,
  useIsCrateApp,
  useIsDarkMode,
  useReadVitaReactiveVariable,
  useVitaReactiveVariableActions,
  useVitaTracking,
} from '@xing-com/crate-lebenslauf-hooks';
import type { FrameProps } from '@xing-com/crate-lebenslauf-xinglets';
import { useHost } from '@xing-com/crate-xinglet';
import { IconArrowRight, IconHamburgerMenu } from '@xing-com/icons';
import type { MenuProps } from '@xing-com/menu';
import { usePopOver } from '@xing-com/pop-over';

import AnschreibenLogoDark from './assets/anschreiben-logo-dark.svg';
import AnschreibenLogoLight from './assets/anschreiben-logo-light.svg';
import appAppleTouchIcon from './assets/apple-touch-icon-precomposed.png';
import appFavicon from './assets/favicon.ico';
import LebenslaufLogoDark from './assets/lebenslauf-logo-dark.svg';
import LebenslaufLogoLight from './assets/lebenslauf-logo-light.svg';
import { Footer } from './footer';
import {
  GlobalStyles,
  StyledBreweryButton,
  StyledDesktopLogin,
  StyledHeader,
  StyledIconButton,
  StyledIconNewChat,
  StyledIconNews,
  StyledIconTopicChannels,
  StyledLink,
  StyledLogo,
  StyledLogoLink,
  StyledMainContent,
  StyledMenu,
  StyledMenuDesktop,
  StyledMobileLogin,
  StyledMobileWrapper,
  StyledNavigation,
  StyledVitaButton,
} from './frame.styled';

export const StyledMenuWrapper: React.FC<
  React.PropsWithChildren<MenuProps>
> = ({ children, ...rest }) => {
  return (
    <>
      <StyledMenu {...rest}>{children}</StyledMenu>
      <StyledMenuDesktop>{children}</StyledMenuDesktop>
    </>
  );
};

const useSelectedNavItem = (): string => {
  const { pathname } = useLocation();

  if (pathname === `/ratgeber/lebenslauf-aufbau-inhalt`) {
    return 'topicChannels';
  }

  if (pathname === `/ratgeber`) {
    return 'news';
  }

  if (pathname === `/vorlagen-und-muster`) {
    return 'newChat';
  }

  return '';
};

export const Frame: React.FC<FrameProps> = ({
  children,
  hideFooter,
  noMainContentWrapperStyles = false,
  isEditorPage = false,
  className,
}) => {
  const { formatMessage } = useIntl();
  const basePath = useBasePath();
  const { pathname } = useLocation();
  const isCoverLetterEditor = useIsCoverLetterEditor();
  const isCoverLetterRoot = useIsCoverLetterRoot();
  const { getHostname } = useHost();
  const isDarkMode = useIsDarkMode();
  const isCrateApp = useIsCrateApp();
  const { showAnschreibenBanner } = useReadVitaReactiveVariable();
  const { toggleShowAnschreibenBanner } = useVitaReactiveVariableActions();
  const { getTrackingPrefixes, trackWithNWT } = useVitaTracking();
  const selectedNavItem = useSelectedNavItem();
  const popOver = usePopOver();

  React.useEffect(() => {
    if (!isCrateApp) toggleShowAnschreibenBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCrateApp]);

  const trackingNwtCreateCoverLetter = (): void => {
    trackWithNWT({
      event: 'opened',
      element: 'create_cover_letter',
      elementDetail: 'link_location_header',
    });
  };

  return (
    <>
      <GlobalStyles />
      <Helmet>
        <link rel="shortcut icon" href={appFavicon} type="image/x-icon" />
        <link rel="apple-touch-icon-precomposed" href={appAppleTouchIcon} />
        <link
          rel="canonical"
          href={`https://${getHostname()}${pathname}`.replace(/\/$/, '')}
        />
      </Helmet>

      <StyledHeader>
        <StyledNavigation
          aria-label={formatMessage({
            id: 'CRATE_LEBENSLAUF_NAVIGATION_LABEL',
          })}
        >
          <StyledLogoLink
            to={`${basePath}/`}
            aria-label={formatMessage({
              id: 'CRATE_LEBENSLAUF_NAVIGATION_LOGO',
            })}
          >
            <StyledLogo
              src={
                isCoverLetterRoot
                  ? isDarkMode
                    ? AnschreibenLogoDark
                    : AnschreibenLogoLight
                  : isDarkMode
                    ? LebenslaufLogoDark
                    : LebenslaufLogoLight
              }
              alt={formatMessage({
                id: 'CRATE_LEBENSLAUF_NAVIGATION_LOGO',
              })}
            />
          </StyledLogoLink>
          <StyledMobileWrapper>
            <StyledDesktopLogin />
            <StyledIconButton
              icon={IconHamburgerMenu}
              size="large"
              onClick={popOver.togglePopOver}
              aria-label={formatMessage({
                id: 'CRATE_LEBENSLAUF_NAVIGATION_OPEN_MENU',
              })}
            />
          </StyledMobileWrapper>
          {!isEditorPage && (
            <StyledMenuWrapper
              dimmerTitle="Close menu"
              onOutsideClick={popOver.handleHide}
              show={popOver.show}
              triggerRef={popOver.triggerRef}
            >
              {!isCoverLetterRoot && (
                <>
                  <StyledLink
                    to={`${basePath}/ratgeber/lebenslauf-aufbau-inhalt`}
                    $opened={selectedNavItem === 'topicChannels'}
                  >
                    <StyledIconTopicChannels width={24} height={24} />
                    <FormattedMessage id="CRATE_LEBENSLAUF_NAVIGATION_STRUCTURE" />
                  </StyledLink>
                  <StyledLink
                    to={`${basePath}/vorlagen-und-muster`}
                    $opened={selectedNavItem === 'newChat'}
                  >
                    <StyledIconNewChat width={24} height={24} />
                    <FormattedMessage id="CRATE_LEBENSLAUF_NAVIGATION_TEMPLATES" />
                  </StyledLink>
                </>
              )}

              <StyledLink
                to={`${basePath}/ratgeber`}
                $opened={selectedNavItem === 'news'}
              >
                <StyledIconNews width={24} height={24} />
                <FormattedMessage id="CRATE_LEBENSLAUF_NAVIGATION_ADVISORY" />
              </StyledLink>

              <StyledMobileLogin />

              {!isEditorPage && !isCoverLetterRoot ? (
                <StyledVitaButton
                  variant="primary"
                  size="medium"
                  sizeConfined="small"
                  data-qa="lebenslauf-header-cta"
                  to={`${basePath}/neu?sc_o=lebenslauf_home_create_cv_click&sc_o_PropActionOrigin=lebenslauf_home_top_navigation`}
                >
                  <FormattedMessage id="CRATE_LEBENSLAUF_NAVIGATION_CREATE_CV" />
                </StyledVitaButton>
              ) : null}
              {!isEditorPage && isCoverLetterRoot ? (
                <StyledVitaButton
                  variant="primary"
                  size="medium"
                  sizeConfined="small"
                  data-qa="anschreiben-header-cta"
                  onClick={trackingNwtCreateCoverLetter}
                  to={`${basePath}/anschreiben?sc_o=${getTrackingPrefixes().host}_home_create_cover_letter_click&sc_o_PropActionOrigin=${getTrackingPrefixes().host}_home_header`}
                >
                  <FormattedMessage id="CRATE_LEBENSLAUF_NAVIGATION_CREATE_COVER_LETTER" />
                </StyledVitaButton>
              ) : null}
              {isCoverLetterEditor && isEditorPage && (
                <StyledBreweryButton
                  variant="proJobs"
                  to={`${basePath}/neu`}
                  size="medium"
                  sizeConfined="small"
                  icon={IconArrowRight}
                >
                  <FormattedMessage id="CRATE_VITA_TEMPLATE_TAG" />
                </StyledBreweryButton>
              )}
            </StyledMenuWrapper>
          )}
        </StyledNavigation>
      </StyledHeader>

      <StyledMainContent
        $noMainContentWrapperStyles={noMainContentWrapperStyles}
        $hasBanner={!!showAnschreibenBanner}
        className={className}
      >
        {children}
      </StyledMainContent>

      {hideFooter ? null : <Footer />}
    </>
  );
};
